import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/track-lazy";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const WAITLIST_ENDPOINT =
  (import.meta.env.VITE_PILOT_FORM_ENDPOINT as string | undefined)?.trim() || "/api/pilot";

type WaitlistFormProps = {
  compact?: boolean;
  source: "hero" | "waitlist-section";
};

const WaitlistForm = ({ compact = false, source }: WaitlistFormProps) => {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!compact) return;

    const focusEmailFromWaitlistHash = () => {
      if (window.location.hash !== "#waitlist") return;

      window.requestAnimationFrame(() => {
        emailInputRef.current?.focus({ preventScroll: true });
      });
    };

    focusEmailFromWaitlistHash();
    window.addEventListener("hashchange", focusEmailFromWaitlistHash);

    return () => window.removeEventListener("hashchange", focusEmailFromWaitlistHash);
  }, [compact]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          _subject: "AntAtlas — Waitlist request",
        }),
      });

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || `HTTP ${response.status}`);
      }

      track("Waitlist Form Submitted", { source });
      setSubmitted(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={`flex items-center gap-2 text-sm font-medium text-foreground ${compact ? "justify-center" : ""}`}
        role="status"
      >
        <Check className="h-4 w-4 text-accent" aria-hidden="true" />
        You&apos;re on the waitlist. We&apos;ll be in touch.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" noValidate={false}>
      <div className={compact ? "flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" : "space-y-4"}>
        <Input
          id="waitlist-email"
          ref={emailInputRef}
          aria-label="Email address"
          autoComplete="email"
          className={compact ? "h-11 sm:flex-1 bg-background/80" : "h-12"}
          placeholder="Work email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button
          variant="accent"
          className={compact ? "h-11 px-6 shrink-0" : "w-full"}
          size={compact ? "default" : "lg"}
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Joining…
            </>
          ) : (
            "Join the waitlist"
          )}
        </Button>
      </div>
      {error && (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </form>
  );
};

export default WaitlistForm;
